import { animate, AnimatePresence, mix, motion, useAnimationFrame, useMotionTemplate, useMotionValue, useTime, useTransform } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import useDebounce from '../../../../utils/hooks/useDebounce';
import { shuffle } from '../../../../utils/utils';

const diceImages = [
    '/pips/d4.png',
    '/pips/d6.png',
    '/pips/d8.png',
    '/pips/d10.png',
    '/pips/d12.png',
    '/pips/d20.png',
]

const randomDiceImages = [
    '/pips/random/random_0.jpg',
    '/pips/random/random_1.jpg',
    '/pips/random/random_2.jpg',
    '/pips/random/random_3.jpg',
    '/pips/random/random_4.jpg',
    '/pips/random/random_5.jpg',
    '/pips/random/random_6.jpg',
]

const degreeRange = mix(0, 360);
const RandomDice: React.FC = () => {

    const animationCursor = useMotionValue(0);
    const borderRotateZ = useTransform(animationCursor, [0, randomDiceImages.length - 1], [45, 45 + 360 * randomDiceImages.length]);
    const borderRotateTemplate = useMotionTemplate`${borderRotateZ}deg`

    const [beat, setBeat] = useState(false);

    useEffect(() => {
        animationCursor.jump(0);
        animate(animationCursor, [0, randomDiceImages.length - .1], { duration: 2, ease: 'easeInOut', delay: .2 });
        setBeat(!beat)
    }, []);

    useDebounce(() => {
        shuffle(randomDiceImages, true)
        animationCursor.jump(0);
        animate(animationCursor, [0, randomDiceImages.length - .1], { duration: 2, ease: 'easeInOut', delay: .2 });
        setBeat(!beat)
    }, 4000, [beat])

    const [imageCursor, setImageCursor] = useState(0);

    useAnimationFrame(() => {
        let i = Math.floor(animationCursor.get())
        if (i !== imageCursor) setImageCursor(i);
    })

    const pathLength = useMotionValue(.5);

    return (
        <div
            className='rounded-full border-4 h-24 sm:h-32 aspect-square bg-gray-900 border-gray-700 flex justify-center items-center p-1 overflow-clip relative'
        >
            <AnimatePresence mode='popLayout'>
                <motion.img
                    key={imageCursor}
                    src={randomDiceImages[imageCursor]}
                    className='size-full object-cover relative z-30'
                    style={{ scale: 1.2 }}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0, transition: { duration: .1 } }}
                    exit={{ opacity: 0, x: 12, transition: { duration: .1 } }}
                />
            </AnimatePresence>
            <motion.svg
                className='absolute top-0 left-0 size-full z-40'
                style={{ rotateZ: borderRotateTemplate }}
                viewBox='0 0 128 128'
            >
                <motion.circle
                    cx='64' cy='64' r='62'
                    strokeWidth={4}
                    stroke='white'
                    fill='transparent'
                    style={{ pathLength }}
                />
            </motion.svg>
        </div>
    )
}

const QuestionMark: React.FC = () => {
    const borderAnimationSpeed = useMotionValue(2000) // ms;
    const bgOpacity = useMotionValue(.30);

    const t = useTime();
    const borderRotateZ = useTransform(() => degreeRange(t.get() / borderAnimationSpeed.get()))
    const borderRotateTemplate = useMotionTemplate`${borderRotateZ}deg`

    // const pathLengthDuration = useState()
    const pathLength = useMotionValue(.5);

    const handleOnHoverStart = () => {
        // animate(borderAnimationSpeed, 3000, { ease: 'easeIn' });
        borderAnimationSpeed.jump(250)
        animate(bgOpacity, .05);
    }

    const handleOnHoverEnd = () => {
        // animate(borderAnimationSpeed, 2000, { ease: 'easeIn' });
        borderAnimationSpeed.jump(2000);
        animate(bgOpacity, .30)
    }

    return (
        <div
            className='rounded-full border-4 size-32 aspect-square bg-black bg-opacity-50 border-gray-700 flex justify-center p-4 relative'
            onPointerEnter={handleOnHoverStart}
            onPointerLeave={handleOnHoverEnd}
        >
            <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 640 640"
                className='h-full'
            >
                <path
                    fill='white'
                    d="M224 224C224 171 267 128 320 128C373 128 416 171 416 224C416 266.7 388.1 302.9 349.5 315.4C321.1 324.6 288 350.7 288 392L288 416C288 433.7 302.3 448 320 448C337.7 448 352 433.7 352 416L352 392C352 390.3 352.6 387.9 355.5 384.7C358.5 381.4 363.4 378.2 369.2 376.3C433.5 355.6 480 295.3 480 224C480 135.6 408.4 64 320 64C231.6 64 160 135.6 160 224C160 241.7 174.3 256 192 256C209.7 256 224 241.7 224 224zM320 576C342.1 576 360 558.1 360 536C360 513.9 342.1 496 320 496C297.9 496 280 513.9 280 536C280 558.1 297.9 576 320 576z"
                />
            </svg>
            <motion.svg
                className='absolute top-0 left-0 size-full'
                style={{ rotateZ: borderRotateTemplate }}
                viewBox='0 0 128 128'
            >
                <motion.circle
                    cx='64' cy='64' r='62'
                    strokeWidth={4}
                    stroke='white'
                    fill='transparent'
                    style={{ pathLength }}
                />
            </motion.svg>
        </div>
    )
}

const TinyDie: React.FC<{ src: string }> = ({ src }) => {
    const name = src.split('/').at(-1)?.split('.')[0];
    const scale = useMotionValue(1.12);

    return (
        <div className='flex flex-col items-center gap-y-2 rounded-md bg-black bg-opacity-50 py-2'>
            <div className='size-24 overflow-visible hover:cursor-pointer'>
                <motion.img className='object-cover' style={{ scale }} whileHover={{ scale: 1.22 }} src={src} />
            </div>
            <div className='px-3'>
                <div className='w-24 h-3 border-l-4 border-b-4 border-r-4 -mb-2 border-gray-300' />
            </div>
            <p className='text-white font-semibold text-2xl'>{name}</p>
        </div>
    )
}

const PipsMysteryBag: React.FC = () => {
    const [embiggen, setEmbiggen] = useState(false);

    return (
        <div className='w-full flex flex-col items-center justify-center gap-y-6 py-6 relative'>

            <QuestionMark />

            <div className='w-96 relative'>
                <motion.img
                    className='w-full shadow-md hover:cursor-pointer border-white border-4'
                    src={'/pips/mystery_bag.jpg'}
                    animate={{ scale: 1 }}
                    whileHover={{ scale: 1.01 }}
                    onClick={() => setEmbiggen(true)}
                />

                {/* Dice preview */}
                <div className='absolute top-0 right-0 translate-x-1/4 -translate-y-1/4 z-30'>
                    <RandomDice />
                </div>
            </div>

            <div className='flex flex-col items-center gap-y-4'>
                <h3 className='text-gray-100 text-3xl bg-black rounded-md bg-opacity-60 px-3 py-1'>Includes one of each...</h3>
                <div className='grid grid-cols-2 sm:grid-cols-3 gap-y-1 gap-x-6'>
                    {diceImages.map((v, i) => <TinyDie key={i} src={v} />)}

                </div>
                <h3 className='text-gray-100 text-3xl w-96 md:w-auto bg-black rounded-md bg-opacity-60 px-4 py-3 text-center'>
                    ...plus one <span className='font-semibold'>percentile die</span> and a 
                    <span className='font-semibold text-yellow-100'>&nbsp;bonus</span> <span className='font-semibold'>metal die!</span>
                </h3>
            </div>

            {/* Expandus */}
            {embiggen && (
                <div className='fixed top-0 left-0 size-full flex justify-center items-center hover:cursor-pointer' onClick={() => setEmbiggen(false)}>
                    <div className='w-4/5 py-6 relative z-50'>
                        <motion.img
                            src={'/pips/mystery_bag.jpg'}
                            style={{ objectFit: 'cover' }}
                            animate={{ scale: 1 }}
                            whileHover={{ scale: 1.05 }}
                        />
                    </div>
                    <div className='absolute top-0 left-0 size-full pointer-events-none bg-black bg-opacity-45 z-10' />
                </div>
            )}
        </div>
    )
}

export default PipsMysteryBag;